import { useTranslation } from 'react-i18next'
import { Form, useFormikContext } from 'formik'
import { Grid, useMediaQuery, useTheme, Skeleton, Box, Divider } from '@mui/material'

import {
  FormTitle,
  MediaInput,
  TextInput,
  PhoneInput,
  CountriesFormInput,
  FormSubmitButton,
  WalletAddressInput,
  IndustriesInput,
  HelperWrapper,
  Loader,
  LocaleSelect,
} from 'src/components'
import { AccountImageInput } from '../account-image-input'
import { ExistsAccountButton } from './exists-account-button.component'
import { Socials } from './socials'
import { Address } from './address'
import { OwnerEmailInput } from './owner-input'
import { AccountNFTMetadataSection } from './nft-metadata'
import { useAccount, useAuth, useMenuItems, usePrompt } from 'src/hooks'

import {
  EAccountSubscriptionPlans,
  ETranslationNs,
  IAccountByIdRespDto,
  IAccountCreateCheckRespDto,
  IAccountFormUpdate,
} from 'src/types'

import useStyles from './styles'
import { COORDS_MULTIPLIER } from 'src/constants'
import {
  DescriptionRichInput,
  DescriptionSimpleInput,
} from 'src/components/products/product-form/fields'
import { AccountCodeCard } from './code-card/account-code-card.component'
import { ImpactsAssociationInput } from './impacts-associations-input.component'

interface IAccountFormProps {
  isAddingNew?: boolean
  isLoading: boolean
  checkData?: IAccountCreateCheckRespDto | void
  disabledSubmit?: boolean
  account?: IAccountByIdRespDto
  locale?: string
  handleLocale?: (locale: string) => void
}

const AccountForm = ({
  isAddingNew,
  disabledSubmit,
  isLoading,
  checkData,
  account,
  locale,
  handleLocale,
}: IAccountFormProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Form])
  const { isSuperadmin } = useAuth()
  const classes = useStyles()
  const { values, handleChange, errors, setFieldValue, isSubmitting, dirty } =
    useFormikContext<IAccountFormUpdate>()
  const theme = useTheme()
  usePrompt({
    message: isLoading ? t('prompt-loading-changes.v') : t('prompt-unsaved-changes.v'),
    when: (dirty && !isSubmitting) || isLoading,
    beforeUnload: true,
  })
  const { data: accountData, isLoading: isAccountLoading } = useAccount()

  const { handleChooseMenuItem, menuItems } = useMenuItems([
    { title: 'account-add-header-title.v', isActive: true, tab: 'profile' },
  ])
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  // const handleDescriptionGenerate = (value: string) => setFieldValue('description', value)

  const handleAddressChange = (val: string) => setFieldValue('address', val)

  const handleCoordsChange = (center: { lat: number; lng: number }) => {
    setFieldValue('latitude', Math.round(center.lat * COORDS_MULTIPLIER))
    setFieldValue('longitude', Math.round(center.lng * COORDS_MULTIPLIER))
  }

  const handlePhoneChange = (v: string) => {
    if (v === '') {
      setFieldValue('phone', null)
      return
    }

    setFieldValue('phone', v)
  }

  const freeAccount = accountData?.subscription_plan === EAccountSubscriptionPlans.free

  if (isAccountLoading) {
    return <Loader />
  }

  return (
    <Form>
      {!isTablet && isAddingNew && (
        <FormTitle
          menuItems={menuItems}
          handleChooseMenuItem={handleChooseMenuItem}
          setFieldValue={setFieldValue}
          title={values.account_holder ?? ''}
          imageComponent={<AccountImageInput />}
        />
      )}
      <Grid className={classes.form}>
        <Grid className={classes.formWrapper} container>
          {locale && handleLocale && (
            <Box
              sx={{
                px: '5px',
                alignSelf: 'start',
              }}
            >
              <LocaleSelect locale={locale} handleLocale={handleLocale} />
            </Box>
          )}
          {!isAddingNew && values.owner_email && (
            <OwnerEmailInput ownerEmail={values.owner_email} />
          )}
          {isTablet && isAddingNew && (
            <Grid className={classes.mainImageWrapper}>
              <AccountImageInput />
            </Grid>
          )}
          <Grid className={classes.upperWrapper}>
            <div className={classes.inputsWrapper}>
              <TextInput
                inputName="account_holder"
                handleChange={handleChange}
                value={values.account_holder}
                error={errors.account_holder ? t(errors.account_holder + '.v') : ''}
                inputPlaceholder={t('account-form-account-holder-input-placeholder.v', {
                  ns: ETranslationNs.Form,
                })}
                inputUpperLabel={t('account-form-account-holder-input-label.v') + '*'}
              />
              <TextInput
                inputName="contact_person"
                handleChange={handleChange}
                value={values.contact_person}
                error={errors.contact_person ? t(`${errors.contact_person}.v`) : ''}
                inputPlaceholder={t('account-form-contact-person-input-placeholder.v', {
                  ns: ETranslationNs.Form,
                })}
                inputUpperLabel={
                  <HelperWrapper t="account-form-contact-person-input-label" isColorInherited />
                }
              />
            </div>
            <div className={classes.inputsWrapper}>
              <PhoneInput
                inputName="phone"
                handleChange={handlePhoneChange}
                value={values.phone}
                error={errors.phone}
                inputPlaceholder={t('account-form-mobile-input-placeholder.v', {
                  ns: ETranslationNs.Form,
                })}
                inputUpperLabel={
                  <HelperWrapper
                    t="account-form-mobile-input-label"
                    ns={ETranslationNs.Form}
                    isColorInherited
                  />
                }
              />
              <TextInput
                inputName="email"
                handleChange={handleChange}
                value={values.email}
                error={errors.email ? t(errors.email + '.v') : ''}
                inputPlaceholder={t('account-form-email-input-placeholder.v', {
                  ns: ETranslationNs.Form,
                })}
                inputUpperLabel={t('account-email-input-label.v')}
              />
            </div>
          </Grid>
          <div className={classes.inputsWrapper}>
            <TextInput
              inputName="billing_email"
              handleChange={handleChange}
              value={values.billing_email}
              error={errors.billing_email ? t(`${errors.billing_email}.v`) : ''}
              inputPlaceholder={t('account-form-billing-email-input-placeholder.v', {
                ns: ETranslationNs.Form,
              })}
              inputUpperLabel={t('account-form-billing-email-input.v')}
            />
            <TextInput
              inputName="website"
              handleChange={handleChange}
              value={values.website}
              error={errors.website}
              inputPlaceholder={t('account-form-website-input-placeholder.v', {
                ns: ETranslationNs.Form,
              })}
              inputUpperLabel={t('account-form-website-input-label.v')}
            />
          </div>
          <Grid className={classes.descriptionWrapper}>
            {isAccountLoading ? (
              <Skeleton variant="rectangular" height={230} width="100%" />
            ) : freeAccount ? (
              <DescriptionSimpleInput
                isRequired={false}
                locale={locale}
                textPrompt={`${t('account-form-ai-prompt.v')} ${
                  values.account_holder || '{account name}'
                } ${t('account-form-ai-prompt-2.v')} ${values.region || '{account region}'} ${
                  values.country || '{account country}'
                }`}
                generationType="Account"
              />
            ) : (
              <DescriptionRichInput
                isRequired={false}
                locale={locale}
                textPrompt={`${t('account-form-ai-prompt.v')} ${
                  values.account_holder || '{account name}'
                } ${t('account-form-ai-prompt-2.v')} ${values.region || '{account region}'} ${
                  values.country || '{account country}'
                }`}
                generationType="Account"
              />
            )}
          </Grid>
          {/* <Grid className={classes.inputWrapper}>
            <TextInput
              inputName="description"
              handleChange={handleChange}
              value={values.description}
              error={errors.description ? t(`${errors.description}.v`) : ''}
              inputPlaceholder={t('account-form-description-input-placeholder.v')}
              inputUpperLabel={t('account-form-description-input-label.v') + '*'}
              multiline={true}
            />
            <GenerateAIButton
              locale={locale}
              textPrompt={`${t('account-form-description-generate-button-text.v')} ${
                values.account_holder || '{account name}'
              } ${t('account-form-description-generate-button-text-1.v')} ${
                values.region || '{region}'
              } ${values.country || '{country}'}`}
              generationType="Account"
              setGeneratedValue={handleDescriptionGenerate}
            />
          </Grid> */}
          <Grid width="100%">
            <CountriesFormInput />
          </Grid>
          <TextInput
            inputName="region"
            handleChange={handleChange}
            value={values.region}
            error={errors.region}
            inputPlaceholder={t('account-form-region-input-placeholder.v', {
              ns: ETranslationNs.Form,
            })}
            inputUpperLabel={t('account-form-region-input-label.v')}
            fullWidth
          />
          <Grid width="100%">
            <Address
              errors={errors}
              address={values.address}
              centerProp={{
                lat: values.latitude ? values.latitude / COORDS_MULTIPLIER : 0,
                lng: values.longitude ? values.longitude / COORDS_MULTIPLIER : 0,
              }}
              handleLatitudeAndLongitude={handleCoordsChange}
              handleAddress={handleAddressChange}
            />
          </Grid>
          <Grid width="100%">
            <IndustriesInput />
          </Grid>

          <ImpactsAssociationInput />

          <MediaInput
            inputName="media"
            media={values.media}
            setFieldValue={setFieldValue}
            noJourneyOptions
            withDnd
          />
          <Socials />
          <div className={classes.inputsWrapper}>
            <TextInput
              inputName="gln"
              handleChange={handleChange}
              value={values.gln}
              error={errors.gln ? t(errors.gln + '.v') : ''}
              inputPlaceholder={t('account-form-gln-input-placeholder.v', {
                ns: ETranslationNs.Form,
              })}
              inputUpperLabel={t('account-form-gln-input-label.v')}
            />
          </div>
          <div className={classes.inputsWrapper}>
            <AccountCodeCard
              outerLng={locale}
              accountId={account?.id!}
              accountSimpleId={account?.simple_id}
            />
          </div>

          {(account?.wallet?.address || (account?.token_id && account?.arweave_url)) && (
            <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', width: '100%' }} />
          )}

          {account?.wallet?.address && (
            <WalletAddressInput withLink fullWidth address={account.wallet.address} />
          )}
          <AccountNFTMetadataSection
            locale={locale}
            isSuperadmin={isSuperadmin}
            isLoading={isLoading}
            account={account}
          />
        </Grid>
        <div className={classes.buttonsWrapper}>
          <>
            <div>
              <FormSubmitButton
                disabled={disabledSubmit}
                withoutIcon={isAddingNew}
                validText={
                  isAddingNew ? (
                    <HelperWrapper t="account-form-submit-button" />
                  ) : (
                    <HelperWrapper t="account-form-submit-button-1" />
                  )
                }
                invalidText={
                  isAddingNew ? (
                    <HelperWrapper t="account-form-submit-button" />
                  ) : (
                    <HelperWrapper t="account-form-submit-button-2" />
                  )
                }
              />
            </div>
            {checkData && <ExistsAccountButton existsData={checkData} />}
          </>
        </div>
      </Grid>
    </Form>
  )
}

export { AccountForm }
